/* eslint-disable */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FormattedMessage, defineMessages, useIntl, injectIntl,
} from 'react-intl';
import { withFormik, Form, Field } from 'formik';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';

import gql from 'graphql-tag';
import { useHistory } from 'react-router-dom';
import Debug from '../../../components/Debug';
import {
  InputTextWithValidationMessage,
  DropdownWithValidation,
} from '../../../components/prime-formik';
import { validationMessages, notificationMessages } from '../../../i18n/common';
import apollo from '../../../services/apollo';
import { AccountType } from '../../../shared/Constants';

const messages = defineMessages({
  inviteUserJoinAccount: { id: 'lbl.invite_user', defaultMessage: 'Invite user to join Account' },
  fullName: { id: 'attrs.fullName', defaultMessage: 'Full Name' },
  email: { id: 'attrs.email', defaultMessage: 'E-mail' },
  role: { id: 'attrs.role', defaultMessage: 'Role' },
  preferredLanguage: { id: 'attr.preferred.language', defaultMessage: 'Preferred language' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
  default: { id: 'user-roles.choose', defaultMessage: '-Choose-' },
  administrator: { id: 'attrs.role.administrator', defaultMessage: 'Administrator' },
  externalSupport: { id: 'attrs.role.external-support', defaultMessage: 'External Support' },
  user: { id: 'attrs.role.user', defaultMessage: 'User' },
});

const CURRENT_ACCOUNT_QUERY = gql`
  query getCurrentAccount($accountId: ID!) {
    account(id: $accountId) {
      id
      type
    }
  }
`;

const LOCALES_QUERY = gql`
  query SupportLocales {
    locales: activeLocales
  }
`;

const UserInvitationForm = ({ currentAccount, isSubmitting }) => {
  const [languages, setLanguages] = useState([]);
  const [currentAccountType, setCurrentAccountType] = useState(null);

  const history = useHistory();
  const { formatMessage } = useIntl();

  const { cldr: { localeDisplayName } } = useSelector(state => state.i18n);

  const getSupportedLanguages = async () => {
    const result = await apollo.query({
      query: LOCALES_QUERY,
    });

    if (result && result.data) {
      const availableSupportedLanguages = result.data.locales.map(
        lang => ({
          value: lang,
          label: localeDisplayName(lang),
        }),
      );

      setLanguages(availableSupportedLanguages);
    }
  };

  const getCurrentAccountType = async () => {
    const result = await apollo.query({
      query: CURRENT_ACCOUNT_QUERY,
      variables: {
        accountId: currentAccount,
      },
    });

    if (result && result.data) {
      const { type } = result.data.account;

      setCurrentAccountType(type);
    }
  };

  useEffect(() => {
    getSupportedLanguages();
    getCurrentAccountType();
  }, []);

  const moveToList = () => {
    const listURL = `/accounts/${currentAccount}/users`;
    history.replace(listURL);
  };

  const getRoles = () => {
    const roles = [];
    roles.push({ value: null, label: formatMessage(messages.default) });

    if (currentAccountType === AccountType.GSC || currentAccountType === AccountType.GP) {
      roles.push({ value: 6, label: formatMessage(messages.administrator) });
      roles.push({ value: 7, label: formatMessage(messages.externalSupport) });
      roles.push({ value: 8, label: formatMessage(messages.user) });
    } else {
      roles.push({ value: 2, label: formatMessage(messages.administrator) });
      roles.push({ value: 3, label: formatMessage(messages.externalSupport) });
      roles.push({ value: 4, label: formatMessage(messages.user) });
    }

    return roles;
  };

  const roles = getRoles();

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel header={formatMessage(messages.inviteUserJoinAccount)}>
            <Form>
              <input type="hidden" name="account" value={currentAccount} />
              <div className="p-grid p-fluid">
                <div className="p-sm-12 p-md-6">
                  <div className="p-grid">
                    <div className="p-col-12">
                      <Field
                        id="fullName"
                        name="fullName"
                        type="text"
                        labelText={formatMessage(messages.fullName)}
                        component={InputTextWithValidationMessage}
                      />
                    </div>
                    <div className="p-col-12">
                      <Field
                        id="email"
                        name="email"
                        labelText={formatMessage(messages.email)}
                        component={InputTextWithValidationMessage}
                        type="text"
                      />
                    </div>
                    <div className="p-col-12">
                      <label className="field-label" htmlFor="role">
                        <FormattedMessage id="attrs.role" defaultMessage="Role" />
                      </label>
                      <Field
                        id="role"
                        name="role"
                        labelText={formatMessage(messages.role)}
                        component={DropdownWithValidation}
                        filter
                        filterBy="label"
                        options={roles}
                      />
                    </div>
                    <div className="p-col-12">
                      <label className="field-label" htmlFor="preferredLanguage">
                        <FormattedMessage id="attrs.preferred.language" defaultMessage="Preferred language" />
                      </label>
                      <Field
                        id="preferredLanguage"
                        name="preferredLanguage"
                        labelText={formatMessage(messages.preferredLanguage)}
                        component={DropdownWithValidation}
                        filter
                        filterBy="label"
                        options={languages}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-grid">
                <div className="p-col-12">
                  <Button
                    type="button"
                    className="p-button-secondary"
                    label={formatMessage(messages.cancel)}
                    onClick={moveToList}
                  />
                  <Button type="submit" label={formatMessage(messages.submit)} disabled={isSubmitting} />
                </div>
              </div>
              <Debug />
            </Form>
          </Panel>
        </div>
      </div>
    </div>
  );
};

function buildValidationSchema(formatMessage) {
  return Yup.object().shape({
    fullName: Yup.string()
      .min(2, formatMessage(validationMessages.tooShort, { value: 2 }))
      .max(50, formatMessage(validationMessages.tooLong, { value: 50 }))
      .required(formatMessage(validationMessages.required))
      .default(''),
    account: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    email: Yup.string()
      .email(formatMessage(validationMessages.invalidEmail))
      .required(formatMessage(validationMessages.required))
      .default(''),
    role: Yup.string()
      .required(formatMessage(validationMessages.required))
      .nullable(validationMessages.required),
    preferredLanguage: Yup.string()
      .required(formatMessage(validationMessages.required))
      .nullable(validationMessages.required),
  });
}

UserInvitationForm.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

async function handleSubmit(values, { props, setSubmitting }) {
  const {
    createUserInvitation,
    growl,
    history,
    currentAccount,
    intl: { formatMessage },
  } = props;
  const {
    email, role, fullName, account, preferredLanguage,
  } = values;

  const fixedRole = role !== 'CHOOSE' ? role : null;
  const fixedLanguage = preferredLanguage !== 'CHOOSE' ? preferredLanguage : null;
  try {
    setSubmitting(true);
    await createUserInvitation.mutation({
      variables: {
        input: {
          email,
          fullName,
          account,
          role: fixedRole,
          preferredLanguage: fixedLanguage,
        },
      },
    });
    setSubmitting(false);
    growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success) });
    const listURL = `/accounts/${currentAccount}/users/invitations`;
    history.push(listURL);
  } catch (error) {
    growl.show({ severity: 'error', detail: error.message, sticky: true });
  }
}

function mapPropsToValues({ intl: { formatMessage }, currentAccount }) {
  // Formik requires initial values for each field, to support error messages on submit
  const validationSchema = buildValidationSchema(formatMessage);

  const initialValues = {
    ...validationSchema.cast(),
    email: '',
    role: null,
    fullName: '',
    account: currentAccount,
    preferredLanguage: null,
  };
  return initialValues;
}

const UserInvitationEnhancedForm = withFormik({
  validationSchema: ({ intl: { formatMessage } }) => buildValidationSchema(formatMessage),
  mapPropsToValues,
  handleSubmit,
})(UserInvitationForm);


export default injectIntl(UserInvitationEnhancedForm);
